import { useState, useCallback, useContext } from "react";
import { GlobleContaxt } from "../../Context/GlobleContaxt";
import { parseString } from "xml2js";

const PORTS = [11100, 11101, 8005];
const PROTOCOLS = ["https", "http"];

// 🔹 PID Options
const buildPidOptions = (deviceType, wadh="") => {
  if (deviceType === "Morpho") {
    return `<PidOptions ver="1.0">
      <Opts fCount="1" fType="2" format="0" pidVer="2.0" timeout="10000" wadh="${wadh || ""}" />
    </PidOptions>`;
  }

  return `<PidOptions ver="1.0">
    <Opts fCount="1" fType="2" iCount="0" pCount="0"
      format="0" pidVer="2.0" timeout="10000" wadh="${wadh || ""}" env="P"/>
    <CustOpts>
      <Param name="mantrakey" value="" />
    </CustOpts>
  </PidOptions>`;
};

// 🔹 Generate URLs
const generateUrls = (deviceType) => {
  let urls = [];

  PORTS.forEach((port) => {
    PROTOCOLS.forEach((protocol) => {
      if (deviceType === "Morpho") {
        urls.push(`${protocol}://127.0.0.1:${port}/rd/capture`);
        urls.push(`${protocol}://localhost:${port}/rd/capture`);
      } else {
        urls.push(`${protocol}://127.0.0.1:${port}/rd/capture`);
        urls.push(`${protocol}://localhost:${port}/rd/capture`);
      }
    });
  });

  return urls;
};

// 🔹 XML → JSON
const convertXmlToJson_ = (xml) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, "text/xml");

  const getAttr = (node, attr) => node?.getAttribute(attr) || "";

  return {
    errCode: getAttr(xmlDoc.getElementsByTagName("Resp")[0], "errCode"),
    errInfo: getAttr(xmlDoc.getElementsByTagName("Resp")[0], "errInfo"),

    ci: getAttr(xmlDoc.getElementsByTagName("Skey")[0], "ci"),
    skey: xmlDoc.getElementsByTagName("Skey")[0]?.textContent,
    hmac: xmlDoc.getElementsByTagName("Hmac")[0]?.textContent,
    pidData: xmlDoc.getElementsByTagName("Data")[0]?.textContent,

    dpId: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "dpId"),
    rdsId: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "rdsId"),
    rdsVer: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "rdsVer"),
    dc: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "dc"),
    mi: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "mi"),
    mc: getAttr(xmlDoc.getElementsByTagName("DeviceInfo")[0], "mc"),
  };
};

export const useAepsFingerprint = () => {
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState(null);

  const { setAlert } = useContext(GlobleContaxt);

  // 🔹 Detect Device (RUN ONLY ONCE)
  const detectDevice = async () => {
    let cached = localStorage.getItem("DEVICE_TYPE");
    if (cached) return cached;

    const urls = [];

    PORTS.forEach((port) => {
      PROTOCOLS.forEach((protocol) => {
        urls.push(`${protocol}://127.0.0.1:${port}/rd/info`);
        urls.push(`${protocol}://127.0.0.1:${port}/info`);
      });
    });

    for (let url of urls) {
      try {
        const res = await fetch(url, { method: "DEVICEINFO" });

        if (res.status === 200) {
          const text = await res.text();

          if (text && text.length > 0) {
            const parser = new DOMParser();
            const xml = parser.parseFromString(text, "text/xml");

            const deviceInfo = xml.getElementsByTagName("DeviceInfo")[0];
            const dpId = deviceInfo?.getAttribute("dpId") || "";

            let detected = "Mantra"; // default

            if (dpId.toUpperCase().includes("MORPHO")) detected = "Morpho";
            if (dpId.toUpperCase().includes("TATVIK")) detected = "Tatvik";

            localStorage.setItem("DEVICE_TYPE", detected);
            return detected;
          }
        }
      } catch {}
    }

    throw new Error("No RD service detected");
  };

 const captureFingerprint = useCallback(async (wadh, deviceType = "Mantra") => {
  setLoading(true);

  try {
    setDevice(deviceType);
    const pidOptions = buildPidOptions(deviceType, wadh);

    let savedUrl = localStorage.getItem("RD_URL");

    let urls = savedUrl
      ? [savedUrl, ...generateUrls(deviceType)]
      : generateUrls(deviceType);

    let finalResponse = null;
    let hasResponse = false;

    // 🔹 Try local RD service URLs
    for (let url of urls) {
      try {
        const res = await fetch(url, {
          method: "CAPTURE",
          headers: { "Content-Type": "text/xml" },
          body: pidOptions,
        });

        const text = await res.text();
        const result = convertXmlToJson_(text);

        if (result && result.errCode !== undefined) {
          hasResponse = true;
          if (result.errCode === "0") {
            localStorage.setItem("RD_URL", url);
            setAlert({ type: "success", msg: "FingerPrint Captured Successfully" });
            finalResponse = text;
          } else {
            setAlert({ type: "error", msg: result?.errInfo || "Capture failed" });
          }

          break;
        }

      } catch (err) {
        continue;
      }
    }

    // 🔹 If not captured via RD → try extension
    if (!finalResponse && !hasResponse) {
      try {
        const resp = await captureFromExtension(wadh, deviceType);

        const result = convertXmlToJson_(resp?.data);
        localStorage.setItem("RD_URL", resp?.url || "");

        console.log("Parsed Result:", result);

        if (result.errCode === "0") {
          setAlert({ type: "success", msg: "FingerPrint Captured Successfully" });
          finalResponse = resp?.data;
        } else {
          setAlert({ type: "error", msg: result?.errInfo || "Capture failed" });
        }

      } catch (err) {
        setAlert({ type: "error", msg: err?.message || "Capture failed" });
        throw err;
      }
    }

    // ✅ FINAL RETURN
    return finalResponse;

  } catch (err) {
    setAlert({ type: "error", msg: err.message });
    throw err;
  } finally {
    setLoading(false); // ✅ always runs
  }

}, []);

 const captureFromExtension = (wadh, deviceType) => {
  return new Promise((resolve, reject) => {
  const pidOptions = buildPidOptions(deviceType, wadh);

    let savedUrl = localStorage.getItem("RD_URL" || "" );
    window.postMessage({
      type: "CAPTURE_RD",
      pidOptions,
      savedUrl: savedUrl || null,
    }, "*");

    const handler = (event) => {
      if (event.data.type === "CAPTURE_RD_RESPONSE") {

        console.log("FULL RESPONSE:", event.data);

        window.removeEventListener("message", handler);

        const res = event.data.response;

        if (res?.success) {
          resolve(res); // XML
        } else {
          reject(new Error(res?.error || "Capture failed"));
        }
      }
    };

    window.addEventListener("message", handler);
  });
};

  return {
    captureFingerprint,
    loading,
    device,
  };
};


export function convertXmlToJs(xmlData) {
    return new Promise((resolve, reject) => {
        parseString(xmlData, (err, result) => {
            if (err) {
                reject(err);
            } else {
                resolve(result);
            }
        });
    });
}

